// Label decluttering (Tim, 15c feedback): names, stationed-ship rows and type icons show only where
// they fit. System markers (dot and rings) are obstacles placed first; candidates are then placed
// greedily by priority in screen pixels, and one that would overlap a marker of another system or
// an already placed candidate stays hidden. Pure, so it is unit-tested without Pixi.
import type { MapSystem } from '@stars-and-ships/api-types';

/** A screen-pixel rectangle. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Candidate {
  key: string;
  /** Lower is placed first. */
  priority: number;
  /** All of a candidate's rectangles show or hide together (a label and its ship row). */
  rects: Rect[];
  /** The system it belongs to: it may cover that system's own marker. */
  systemId?: string;
  /** Shifts of all rects to try in order; none = only where it is. */
  offsets?: Offset[];
}

/** A screen-pixel shift. */
export interface Offset {
  dx: number;
  dy: number;
}

const NO_OFFSET: Offset = { dx: 0, dy: 0 };

/** A system's marker; never covered by anything of another system. */
export interface Obstacle {
  systemId: string;
  rect: Rect;
}

interface Placed {
  rect: Rect;
  /** Set for markers only. */
  systemId?: string;
}

/** Pixels kept free around every placed rectangle. */
export const DECLUTTER_PAD_PX = 2;
const CELL_PX = 64;

const overlaps = (a: Rect, b: Rect, pad: number) =>
  a.x < b.x + b.w + pad && b.x < a.x + a.w + pad && a.y < b.y + b.h + pad && b.y < a.y + a.h + pad;

/**
 * The candidates that fit, by priority then key (stable between frames), each with the first of
 * its offsets that fits.
 */
export function declutter(
  candidates: readonly Candidate[],
  obstacles: readonly Obstacle[] = [],
  pad = DECLUTTER_PAD_PX
): Map<string, Offset> {
  const order = [...candidates].sort(
    (a, b) => a.priority - b.priority || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
  );
  const grid = new Map<string, Placed[]>();
  const cellsOf = (r: Rect) => {
    const out: string[] = [];
    const x0 = Math.floor((r.x - pad) / CELL_PX);
    const x1 = Math.floor((r.x + r.w + pad) / CELL_PX);
    const y0 = Math.floor((r.y - pad) / CELL_PX);
    const y1 = Math.floor((r.y + r.h + pad) / CELL_PX);
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) out.push(`${x},${y}`);
    return out;
  };
  const place = (placed: Placed) => {
    for (const cell of cellsOf(placed.rect)) {
      const list = grid.get(cell);
      if (list) list.push(placed);
      else grid.set(cell, [placed]);
    }
  };
  // Markers may overlap each other; they only block what is placed after them.
  for (const o of obstacles) place({ rect: o.rect, systemId: o.systemId });
  const blocked = (r: Rect, systemId: string | undefined) =>
    cellsOf(r).some((cell) =>
      grid
        .get(cell)
        ?.some(
          (placed) =>
            !(placed.systemId !== undefined && placed.systemId === systemId) &&
            overlaps(r, placed.rect, pad)
        )
    );
  const shown = new Map<string, Offset>();
  for (const c of order) {
    const rects = c.rects.filter((r) => r.w > 0 && r.h > 0);
    for (const offset of c.offsets ?? [NO_OFFSET]) {
      const moved = rects.map((r) => ({ ...r, x: r.x + offset.dx, y: r.y + offset.dy }));
      if (moved.some((r) => blocked(r, c.systemId))) continue;
      shown.set(c.key, offset);
      for (const rect of moved) place({ rect });
      break;
    }
  }
  return shown;
}

/** Joins candidates with the same key: their rectangles, the best priority. */
export function mergeCandidates(candidates: readonly Candidate[]): Candidate[] {
  const byKey = new Map<string, Candidate>();
  for (const c of candidates) {
    const seen = byKey.get(c.key);
    if (seen) {
      seen.rects.push(...c.rects);
      seen.priority = Math.min(seen.priority, c.priority);
    } else {
      byKey.set(c.key, { ...c, rects: [...c.rects] });
    }
  }
  return [...byKey.values()];
}

export const labelKey = (systemId: string) => `label:${systemId}`;
export const iconKey = (systemId: string) => `icon:${systemId}`;
export const sectorKey = (sectorId: string) => `sector:${sectorId}`;

/**
 * Who wins a crowded spot: sector names, then the selected system, own systems (the home system
 * has no head start, Tim round 2), alliance members, other owners, unowned systems; type icons
 * give way to every name.
 */
export const PRIORITY = {
  sector: 0,
  selected: 1,
  own: 2,
  ally: 3,
  owned: 4,
  unowned: 5,
  icon: 6,
} as const;

export function labelPriority(
  system: MapSystem,
  viewer: {
    playerId: string | undefined;
    allianceId: string | null | undefined;
    selectedId: string | null;
  }
): number {
  if (system.id === viewer.selectedId) return PRIORITY.selected;
  if (!system.owner) return PRIORITY.unowned;
  if (viewer.playerId !== undefined && system.owner.playerId === viewer.playerId) {
    return PRIORITY.own;
  }
  if (viewer.allianceId && system.owner.alliance?.id === viewer.allianceId) return PRIORITY.ally;
  return PRIORITY.owned;
}
